// =============================================================================
// _shared/kinFeedRows.ts — the pure decisions behind kin-calendar-feed (#1647)
// =============================================================================
//
// Everything here is testable from vitest with no Deno, no client, no clock
// unless one is passed in. kin-calendar-feed/index.ts is the HTTP shell: it
// fetches, calls these, and serves. Keeping the decisions here is what lets
// the mutation gate reach them — a Deno handler cannot be imported by vitest.
// =============================================================================

import { EVENT_NOT_A_TASK_FILTER, isEventVisible } from "./eventVisibility.ts";
import type { EventRow } from "./icsBuilder.ts";
import { suppressPlanDuplicatesOfImports } from "./suppressPlanDuplicates.ts";

/** How far back the feed reaches. Apple keeps what it has already pulled, so
 *  this only bounds the file, not her history. */
export const FEED_LOOKBACK_DAYS = 60;

/** Hard cap on rows per file, same spirit as TodayTab's EVENTS_QUERY_ROW_LIMIT:
 *  bounded and ordered, so any truncation is at the far end and reproducible. */
export const FEED_ROW_LIMIT = 2000;

/** PostgREST `.or()` filter the handler applies BEFORE the row limit, so a
 *  user with thousands of to-dos in the window cannot push her real events
 *  past the cap (Copilot on #1648). `isFeedRow` re-applies the same rule
 *  after the fetch, as defense in depth; the two must agree. */
/**
 * ⚠️ #1696 — re-exported from `eventVisibility.ts` rather than declared here.
 * This constant was the closest thing the repo had to a shared events rule, and
 * `src/lib/voiceScheduleRead.ts` already imports it from this module, so the name
 * stays put. The DEFINITION moved, so there is one of it.
 */
export const FEED_EVENT_CATEGORY_FILTER = EVENT_NOT_A_TASK_FILTER;

/** #1816 — the table the calendar merge writes into. A row from a calendar
 *  only ever lands in `events` through the week blob, so this is the one
 *  `source_table` a calendar-origin row can carry. */
export const PLAN_MIRROR_TABLE = "weekly_schedules";

/** #1816 / #1828 — the one provider whose rows are an ECHO when served to Apple.
 *  Her Apple events are already in Apple Calendar; sending them back shows each
 *  one twice. Google is deliberately NOT here (Kasandra, 2026-10-14, #1828):
 *  when Google Calendar is connected, her Google events are part of her Kin
 *  week and flow onto the "Kin" calendar with everything else. */
export const FEED_ECHO_PROVIDER = "apple";

/** #1828 — PostgREST `.or()` filter for the provider half of the rule, applied
 *  by the handler BEFORE the row limit (a mom with 2,000 Apple events must not
 *  push her Kin events past the cap). Reads: keep the row unless its provider
 *  is the echo provider.
 *
 *  ⚠️ Two clauses on purpose. `.neq("provider", "apple")` alone is SQL
 *  `provider <> 'apple'`, which is NULL — i.e. FALSE — for every row with no
 *  provider, and that is every Kin-authored row. The `is.null` clause is what
 *  keeps her own events in the feed. */
export const FEED_PROVIDER_FILTER = `provider.is.null,provider.neq.${FEED_ECHO_PROVIDER}`;

/** #1816 — PostgREST `.or()` filter for the legacy half of the rule, applied
 *  alongside FEED_PROVIDER_FILTER. `isEchoBackToApple` re-applies both per row;
 *  the "handler's constants agree with the pure rules" test pins that they match.
 *
 *  Reads: keep the row unless it is UNTAGGED and carries a synthetic
 *  `google_event_id` AND is the plan's mirror. All three halves matter:
 *  - a row written before #487 has no `provider` but still carries the id the
 *    merge stamped on it — unattributable between Apple and Google, so it stays
 *    excluded: the duplicate she reported must not come back through it;
 *  - a Kin-authored import SHE pushed to Google (Save to Kin + Google Calendar,
 *    #1806) carries a real Google id on an `imported_events` row — hers, stays
 *    (Copilot on PR #1819);
 *  - a TAGGED Google row also carries an id on the plan mirror, and since #1828
 *    it flows, so the first clause lets any tagged row through this filter and
 *    leaves the decision to FEED_PROVIDER_FILTER. */
export const FEED_LEGACY_ORIGIN_FILTER = `provider.not.is.null,google_event_id.is.null,source_table.neq.${PLAN_MIRROR_TABLE}`;

/** How long a client may reuse a feed response. "Stop showing" promises the
 *  link is dead within a minute; a 300s cache would have let Calendar keep
 *  showing a revoked set for five (Copilot on #1648). */
export const FEED_CACHE_MAX_AGE_SECONDS = 60;
export const FEED_CACHE_CONTROL = `private, max-age=${FEED_CACHE_MAX_AGE_SECONDS}`;

/** The row the token lookup returns. */
export interface FeedTokenRow {
  user_id: string;
  revoked_at: string | null;
}

/** Which user a feed token unlocks — or nobody.
 *
 *  ⚠️ "Stop showing" in Settings does not delete the row; it stamps revoked_at.
 *  This check is the ONLY thing that makes a revoked link go dead. Apple keeps
 *  polling a subscribed URL indefinitely, so a revoked token that still served
 *  would keep publishing her calendar to whoever held the link. */
export function activeFeedUser(row: FeedTokenRow | null | undefined): string | null {
  if (!row) return null;
  if (row.revoked_at !== null) return null;
  return row.user_id;
}

/** Token shape: two crypto.randomUUID()s with the dashes stripped — 64 hex
 *  chars, 244 bits. Anything else is a 404 before the database is touched. */
export const FEED_TOKEN_RE = /^[0-9a-f]{64}$/i;

/** The first event_date the feed includes, as YYYY-MM-DD (UTC calendar day). */
export function feedWindowStart(now: Date): string {
  const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - FEED_LOOKBACK_DAYS));
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

/** The subset of an `events` row the feed reads.
 *
 *  A `type`, not an `interface`, on purpose (#1816): `suppressPlanDuplicatesOfImports`
 *  takes rows shaped `{ …; [k: string]: unknown }`, and TypeScript grants an
 *  object-literal TYPE that implicit index signature while denying it to an
 *  interface — so the interface form fails `deno check` with TS2345 at the call. */
export type FeedSourceRow = {
  id: string;
  title: string;
  event_date: string;
  /** #121's last-day marker for a multi-day event, or null for a single day.
   *  #1885 — the feed never selected it, so school breaks, camps and trips
   *  reached Apple Calendar on their first day only. Optional so a caller
   *  written before this still type-checks; absent reads as single-day, which
   *  is exactly what every row looked like before. */
  end_date?: string | null;
  start_time: string | null;
  is_all_day: boolean | null;
  location: string | null;
  notes: string | null;
  status: string;
  is_dismissed: boolean | null;
  category: string | null;
  updated_at?: string | null;
  /** #1966 — minutes before the event to alarm, or null for none. Optional so
   *  a caller written before the column still type-checks; absent reads as "no
   *  stored reminder", which is what every row looked like before. ⚠️ MUST be
   *  named in the reader's explicit select list or it arrives as `undefined`
   *  with no error and every alarm silently disappears. */
  reminder_minutes?: number | null;
  /** #1816 — read to DECIDE, never emitted. Which table mirrored this row
   *  (`weekly_schedules` is the plan's copy; everything else is a real source),
   *  and the two marks that say it came FROM a calendar. Optional so a caller
   *  that pre-dates #1816 still type-checks; absent reads as "not from a
   *  calendar", which is what every row looked like before the fix. */
  source_table?: string | null;
  provider?: string | null;
  google_event_id?: string | null;
};

/** #1816 / #1828 — would serving this row to Apple be an ECHO of her own Apple
 *  Calendar?
 *
 *  `apple-calendar-feed` reads her iCloud events into the week; the Google
 *  merge does the same for Google. `persistSchedule` then mirrors every blob
 *  entry into `events`, so those rows sit next to the ones Kin authored. Today
 *  knows the difference (it reconciles them by provider); this feed did not,
 *  and sent her own Apple events straight back to Apple — every one of them
 *  showing twice in Apple Calendar, once in its own calendar and once in
 *  "Kin". 23 of the 68 rows her feed served on 2026-10-11 were this.
 *
 *  ⚠️ #1828 (2026-10-14) REVERSED #1816's exclusion of Google-origin rows.
 *  #1816 excluded them on the reasoning that the feed shows Kin, not a relay.
 *  Kasandra's call: when Google Calendar is connected, her Google events ARE
 *  her Kin week and belong on the "Kin" calendar. So the rule is no longer
 *  "from a calendar, never to a calendar" — it is "never echo Apple to Apple".
 *
 *  Two marks, and the second is deliberately narrow:
 *  - `provider === "apple"` → read in from her iCloud. Echo. Out.
 *  - `provider` null AND `google_event_id` set AND `source_table` is the plan
 *    mirror → a row the merge stamped before #487 existed. Unattributable
 *    between Apple and Google, so it stays out: the duplicate she reported must
 *    not come back through it. A `google_event_id` on an `imported_events` row
 *    is the OPPOSITE direction — Kin pushed her import TO Google (#1806) — and
 *    that row is hers; it stays on the feed. */
export function isEchoBackToApple(row: FeedSourceRow): boolean {
  if (row.provider === FEED_ECHO_PROVIDER) return true;
  if (row.provider != null) return false;
  return row.google_event_id != null && row.source_table === PLAN_MIRROR_TABLE;
}

/** Does this events row belong on her Apple Calendar?
 *
 *  Events only — a to-do is not a calendar entry, and the day card draws the
 *  same line (dayCardPartition.ts). `category NULL` is an event: #1637 made
 *  that the read-side rule for imports, and a manual row with no category was
 *  always drawn on the schedule. Dismissed and non-active rows are gone from
 *  her week, so they are gone from Apple too. And her own Apple events are
 *  never echoed back to Apple (#1816; Google flows since #1828). */
export function isFeedRow(row: FeedSourceRow): boolean {
  // #1816 / #1828 — the one rule in one sentence: never echo Apple to Apple.
  if (isEchoBackToApple(row)) return false;
  // #1696 — the visibility half now comes from the one shared rule rather than
  // being re-stated here. This function was RIGHT; it was one of only two
  // readers that were. It moves onto the shared predicate anyway, because a
  // correct hand-written copy is still a copy, and six of the other seven
  // readers prove what happens to copies of this rule.
  if (!isEventVisible(row)) return false;
  // The category half stays local: it is a SEPARATE rule that only the surfaces
  // drawing a schedule want (see eventVisibility.ts's header — the meal planner
  // and the gift read legitimately read a category 'task' row).
  if (row.category === "task") return false;
  return true;
}

/** Suffix a TBD row wears in the feed, so it is visible without inventing a time. */
export const TBD_TITLE_SUFFIX = " · time TBD";

/** events row → builder row.
 *
 *  A TBD row (no time, NOT all-day — #607's distinction) becomes an all-day
 *  banner with " · time TBD" on the title. The one-shot handoff gives the same
 *  row a 9:00 AM placeholder; in a calendar Apple re-pulls for months, a fake
 *  9:00 would read as a fact she never stated. Kasandra can override this;
 *  see #1647's TASK 2. */
export function toFeedEventRow(row: FeedSourceRow): EventRow {
  const tbd = !row.is_all_day && !row.start_time;
  const allDay = tbd ? true : !!row.is_all_day;
  return {
    id: row.id,
    title: tbd ? `${row.title}${TBD_TITLE_SUFFIX}` : row.title,
    event_date: row.event_date,
    // #1885 — only for a REAL all-day row, and only when it actually spans
    // more than one day. Two deliberate silences:
    //   - a TIMED row ignores end_date entirely. Its DTEND is its start plus an
    //     hour, and a date-valued end on a date-time start is not a longer
    //     event, it is a malformed VEVENT.
    //   - a TBD row that this function converts to all-day keeps single-day
    //     behaviour. We invented that banner because there was no time; it must
    //     not also invent a length.
    end_date: allDay && !tbd && isAfter(row.end_date, row.event_date) ? row.end_date : null,
    start_time: tbd ? null : row.start_time,
    is_all_day: allDay,
    location: row.location,
    notes: row.notes,
    updated_at: row.updated_at ?? null,
    // #1966 — carried through so the SUBSCRIBED calendar emits a VALARM. The
    // feed passes "none" as buildIcsFile's batch argument, so this column is
    // the ONLY thing that can put an alarm on her Kin calendar.
    //
    // ⚠️ `?? null` and never `|| null`: a stored 0 is "alarm at the time of
    // the event", a real option in her picker, and `||` would erase it.
    reminder_minutes: row.reminder_minutes ?? null,
  };
}

/** Strictly after, on "YYYY-MM-DD" strings, which sort lexicographically.
 *  Equal or earlier is not a span: #121 writes end_date on single-day rows in
 *  some paths, and a DTEND at or before DTSTART is a VEVENT Apple will refuse
 *  or silently collapse. */
function isAfter(end: string | null | undefined, start: string): boolean {
  return typeof end === "string" && end.length > 0 && end > start;
}

/** Filter + map + order, in one place, so the handler has nothing to get wrong.
 *
 *  #1816 — the plan's copy of an import is dropped FIRST, by the same rule
 *  Today and the morning note already use (`suppressPlanDuplicatesOfImports`,
 *  one definition in `_shared`). The model sometimes re-adds an event she
 *  imported from a screenshot despite being told not to; Today hides that
 *  copy, this feed did not, so "Startup World Cup…" arrived on Apple as two
 *  VEVENTs with two UIDs. It runs before `isFeedRow` on purpose: suppression
 *  needs the imported row still present to recognise its twin. */
export function buildFeedRows(rows: FeedSourceRow[]): EventRow[] {
  return suppressPlanDuplicatesOfImports(rows)
    .filter(isFeedRow)
    .map(toFeedEventRow)
    .sort((a, b) =>
      a.event_date === b.event_date
        ? (a.start_time ?? "").localeCompare(b.start_time ?? "")
        : a.event_date.localeCompare(b.event_date)
    );
}
